import { adminUsersApi } from '@/api/admin/adminUsersApi';
import {
  adminUserDetailApi,
  type OwnedClub,
  type PaymentMovement,
} from '@/api/admin/adminUserDetailApi';
import { RoleBadge, StatusBadge } from '@/components/admin/users/UserBadges';
import { UserActionsMenu } from '@/components/admin/users/UserActionsMenu';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminUser } from '@/types/admin.model';
import { handleGlobalError } from '@/utils/error-handler';
import { currencyFormatter } from '@/utils/movements-utils';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

const AdminUsuarioDetallePage = () => {
  const { pk } = useParams<{ pk: string }>();
  const location = useLocation();
  // Handed over by UsersTable purely so the header paints on the first frame;
  // the fetch below is what the screen actually trusts. Guarded against the URL
  // and the history entry disagreeing, which would show another user's data.
  const handedOver = (location.state as { user?: AdminUser } | null)?.user;
  const preloaded = handedOver && handedOver.pk === pk ? handedOver : null;
  const [user, setUser] = useState<AdminUser | null>(preloaded);
  const [clubs, setClubs] = useState<OwnedClub[]>([]);
  const [movements, setMovements] = useState<PaymentMovement[]>([]);
  // Starts false when the table handed a row over, or the handover is dead
  // code: an unconditional skeleton returns before any header renders, so the
  // preloaded user never reaches a frame and `setUser(found)` overwrites it.
  //
  // It also never goes back up. A refetch — the `onDone` of a ban, an unban, a
  // role change — would otherwise blank the whole record to a skeleton and
  // unmount the very menu the admin acted from.
  const [isInitialLoading, setIsInitialLoading] = useState(!preloaded);
  // Distinguishes "the fetch failed" from "the user genuinely has none", which
  // Promise.allSettled and a caught list-users error would otherwise hide.
  const [clubsFailed, setClubsFailed] = useState(false);
  const [movementsFailed, setMovementsFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (!pk) return;
    setClubsFailed(false);
    setMovementsFailed(false);
    setLoadFailed(false);
    try {
      const found = await adminUsersApi.getUser(pk);
      setUser(found);

      if (found) {
        // Neither side blocks the screen: a user with no clubs or no movements is normal.
        const [clubsResult, movementsResult] = await Promise.allSettled([
          adminUserDetailApi.getOwnedClubs(found.email),
          adminUserDetailApi.getMovements(found.email),
        ]);
        setClubs(clubsResult.status === 'fulfilled' ? clubsResult.value : []);
        setClubsFailed(clubsResult.status === 'rejected');
        setMovements(
          movementsResult.status === 'fulfilled' ? movementsResult.value : [],
        );
        setMovementsFailed(movementsResult.status === 'rejected');
      }
    } catch (error) {
      // Cleared on purpose: the row handed over by the table would otherwise stay
      // on screen as if it had loaded, hiding the failure behind stale data.
      setUser(null);
      setLoadFailed(true);
      handleGlobalError(error, 'No se pudo cargar el usuario', 'Revisá la conexión e intentá de nuevo.');
    } finally {
      setIsInitialLoading(false);
    }
  }, [pk]);

  useEffect(() => {
    load();
  }, [load]);

  if (isInitialLoading) {
    return <Skeleton className="m-6 h-32" />;
  }

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">
          {loadFailed ? 'No se pudo cargar el usuario.' : 'Usuario no encontrado.'}
        </p>
        <Link to="/admin/usuarios" className="text-sm text-blue-700 hover:underline">
          Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <Link to="/admin/usuarios" className="text-sm text-blue-700 hover:underline">
        ← Volver al listado
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {user.name || user.email}
          </h1>
          <p className="text-sm text-gray-600">{user.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1">
            {(user.roles ?? []).map((role) => (
              <RoleBadge key={role} role={role} />
            ))}
            <StatusBadge user={user} />
          </div>
        </div>
        <UserActionsMenu user={user} onDone={load} />
      </div>

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold text-gray-900">
          Clubes donde es owner
        </h2>
        {clubsFailed ? (
          <p className="text-sm text-gray-500">No se pudieron cargar los clubes.</p>
        ) : clubs.length === 0 ? (
          <p className="text-sm text-gray-500">No tiene clubes.</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-700">
            {clubs.map((club) => (
              <li key={club.id}>
                {club.name}
                {club.status ? ` — ${club.status}` : ''}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold text-gray-900">
          Últimos movimientos
        </h2>
        {movementsFailed ? (
          <p className="text-sm text-gray-500">No se pudieron cargar los movimientos.</p>
        ) : movements.length === 0 ? (
          <p className="text-sm text-gray-500">Sin movimientos registrados.</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-700">
            {movements.slice(0, 20).map((movement, index) => (
              <li key={movement.paymentId ?? `${movement.paidAt}-${index}`} className="flex justify-between">
                <span>
                  {movement.paidAt
                    ? new Date(movement.paidAt).toLocaleDateString('es-AR')
                    : '—'}
                  {movement.tournamentName ? ` — ${movement.tournamentName}` : ''}
                </span>
                <span className="font-medium">
                  {movement.amount == null
                    ? '—'
                    : currencyFormatter.format(movement.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default AdminUsuarioDetallePage;
